import Color from '@/Enums/Color.enum';
import type InventoryType from '@/Enums/InventoryType.enum';
import type { IInventoryTab } from '@/hooks/useInventoryTabs';
import { Tab, Tabs, tabClasses, tabsClasses } from '@mui/material';
import classNames from 'classnames';

interface IProps {
  tabs: IInventoryTab[];
  activeTab: InventoryType;
  onChange: (tab: InventoryType) => void;
  className?: string;
}

const CountBadge = ({ count, active }: { count?: number; active?: boolean }) =>
  count ? (
    <span
      className={classNames('min-w-[20px] px-1.5 rounded-full text-[12px] text-center', {
        'bg-[#E8E8E8] text-[#303030]': !active,
        'text-white': active,
      })}
      style={active ? { backgroundColor: Color.Primary } : undefined}
    >
      {count}
    </span>
  ) : null;

const ProductInventoryTabs = ({ tabs, activeTab, onChange, className }: IProps) => {
  if (tabs.length < 2) {
    return (
      <div
        className={classNames(
          'flex items-center font-[Manrope] text-[14px] font-semibold',
          `text-[${Color.Grey}]`,
          className,
        )}
      >
        <span className='flex items-center gap-2 py-2'>
          {tabs[0]?.label ?? 'Publishers'}
          <CountBadge count={tabs[0]?.count} />
        </span>
      </div>
    );
  }

  return (
    <Tabs
      value={activeTab}
      onChange={(_, tab: InventoryType) => onChange(tab)}
      aria-label='Inventory type'
      className={className}
      sx={{
        minHeight: 'unset',
        [`& .${tabsClasses.flexContainer}`]: { gap: '16px' },
        [`& .${tabsClasses.indicator}`]: { backgroundColor: Color.Primary },
        [`& .${tabClasses.root}`]: {
          minHeight: 'unset',
          minWidth: 'unset',
          padding: '8px 0 6px',
          fontFamily: 'Manrope',
          fontSize: '14px',
          fontWeight: 600,
          color: Color.Grey,
          '&:hover': { color: '#303030' },
        },
        [`& .${tabClasses.selected}`]: { color: '#303030' },
      }}
    >
      {tabs.map(tab => (
        <Tab
          key={tab.type}
          value={tab.type}
          disableRipple
          label={
            <span className='flex items-center gap-2'>
              {tab.label}
              <CountBadge count={tab.count} active={tab.type === activeTab} />
            </span>
          }
        />
      ))}
    </Tabs>
  );
};

export default ProductInventoryTabs;
